
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { contextSyncService } from '@/services/contextSync';
import { EventBus } from '@/main';

interface ConstraintInfo {
  value: number;
  satisfied: boolean;
}

interface StockoutRisk {
  days_until_stockout: number;
  urgency_level: 'critical' | 'warning';
}

interface HistoricalData {
  avg_monthly_sales?: number;
  sales_trend?: 'growing' | 'declining' | 'stable';
  profit_margin?: number;
  total_units_sold?: number;
}

interface ItemAnalysis {
  productName: string;
  variantName?: string;
  sku: string;
  orderedQuantity: number;
  suggestedQuantity: number;
  unitPrice: number;
  lineTotal: number;
  currentInventory: number;
  coverageDays: number;
  estimatedCoverage: number;
  status: string;
  statusReason?: string;
  constraints?: {
    moq: ConstraintInfo;
    max_qty: ConstraintInfo;
  };
  stockout_risk?: StockoutRisk | null;
  historical?: HistoricalData;
  // Allow additional fields from backend with different naming conventions
  [key: string]: unknown;
}

interface Recommendation {
  product?: string;
  sku?: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  type?: string;
  constraint_type?: string;
  shortfall?: number;
}

interface ReviewSummary {
  totalItems: number;
  totalOrderedUnits: number;
  totalSuggestedUnits: number;
  totalValue: number;
  averageCoverageDays: number;
  hasErrors: boolean;
  hasWarnings: boolean;
  supplier_id: number;
  supplier_name: string;
  mov_required: number | null;
  mov_satisfied: boolean;
  mov_shortfall: number | null;
  critical_stockout_items: number;
}

/** Supplier constraints from backend */
interface SupplierConstraints {
  min_order_value?: number | null;
  max_order_value?: number | null;
  min_order_quantity?: number | null;
  max_order_quantity?: number | null;
  lead_time?: number | null;
  reorder_cycle?: number | null;
  payment_terms?: string | null;
}

interface POReviewResultsParameters {
  draft_po: unknown;
  supplier_id: number;
  supplier_name: string;
  review_summary: ReviewSummary;
  items_analysis: ItemAnalysis[];
  recommendations: Recommendation[];
  supplier_constraints?: SupplierConstraints;
  ai_insight?: string;
}

@Component({
  name: 'POReviewResultsDisplay'
})
export default class POReviewResultsDisplay extends Vue {
  @Prop({ default: null }) readonly chatResponse!: unknown;
  @Prop({ default: () => ({}) }) readonly actionParameters!: POReviewResultsParameters;

  private itemFilter = 'all';

  mounted() {
    this.syncScreenContext();
  }

  @Watch('actionParameters', { deep: true })
  onActionParametersChanged() {
    this.syncScreenContext();
  }

  /**
   * Sync screen context to contextSyncService for automatic context attachment.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId()) {
      return;
    }

    if (!this.hasData) {
      return;
    }

    contextSyncService.updateScreenContext({
      screen_name: 'po_review_results',
      screen_path: '/purchase-assistant',
      screen_params: {
        supplier_id: this.supplierId,
        supplier_name: this.supplierName,
      },
      component_state: {
        screen_type: 'po_review_results',
        screen_data: {
          supplier_id: this.supplierId,
          supplier_name: this.supplierName,
          total_items: this.reviewSummary.totalItems,
          total_value: this.reviewSummary.totalValue,
          mov_satisfied: this.movSatisfied,
          mov_shortfall: this.movShortfall,
          mov_required: this.movRequired,
          critical_stockout_items: this.criticalStockoutCount,
          has_errors: this.hasBlockingErrors,
          has_warnings: this.hasWarnings,
          items_count: this.itemsAnalysis.length,
          recommendations_count: this.recommendations.length,
        },
      },
    });
  }

  // Computed properties
  get hasData(): boolean {
    return this.itemsAnalysis.length > 0;
  }

  get supplierId(): number {
    return this.actionParameters?.supplier_id || 0;
  }

  get supplierName(): string {
    return this.actionParameters?.supplier_name || 'Unknown Supplier';
  }

  get reviewSummary(): ReviewSummary {
    return this.actionParameters?.review_summary || {
      totalItems: 0,
      totalOrderedUnits: 0,
      totalSuggestedUnits: 0,
      totalValue: 0,
      averageCoverageDays: 0,
      hasErrors: false,
      hasWarnings: false,
      supplier_id: 0,
      supplier_name: '',
      mov_required: null,
      mov_satisfied: true,
      mov_shortfall: null,
      critical_stockout_items: 0,
    };
  }

  get itemsAnalysis(): ItemAnalysis[] {
    return this.actionParameters?.items_analysis || [];
  }

  get recommendations(): Recommendation[] {
    return this.actionParameters?.recommendations || [];
  }

  get orderLevelRecommendations(): Recommendation[] {
    return this.recommendations.filter(r => r.type === 'order_level');
  }

  get allRecommendations(): Recommendation[] {
    return this.recommendations;
  }

  // INSIGHT section computed properties
  get aiInsight(): string {
    return this.actionParameters?.ai_insight || '';
  }

  // DATA section computed properties
  get supplierConstraints(): SupplierConstraints {
    return this.actionParameters?.supplier_constraints || {};
  }

  get supplierLeadTime(): number | null {
    return this.supplierConstraints.lead_time || null;
  }

  get supplierMinQty(): number | null {
    return this.supplierConstraints.min_order_quantity || null;
  }

  get supplierMaxQty(): number | null {
    return this.supplierConstraints.max_order_quantity || null;
  }

  get supplierMaxOrderValue(): number | null {
    return this.supplierConstraints.max_order_value || null;
  }

  get supplierReorderCycle(): number | null {
    return this.supplierConstraints.reorder_cycle || null;
  }

  get movProgress(): number {
    if (!this.movRequired) return 100;
    const progress = (this.reviewSummary.totalValue / this.movRequired) * 100;
    return Math.min(progress, 100);
  }

  get hasHistoricalData(): boolean {
    return this.itemsAnalysis.some(item =>
      item.historical?.avg_monthly_sales !== undefined ||
      item.historical?.sales_trend !== undefined
    );
  }

  get itemsWithMoq(): number {
    return this.itemsAnalysis.filter(item =>
      item.constraints?.moq && item.constraints.moq.value > 0
    ).length;
  }

  // Calculated totals
  get totalOrderedUnits(): number {
    return this.itemsAnalysis.reduce((sum, item) => sum + (item.orderedQuantity || 0), 0);
  }

  get totalSuggestedUnits(): number {
    return this.itemsAnalysis.reduce((sum, item) => sum + (item.suggestedQuantity || 0), 0);
  }

  get suggestedTotalValue(): number {
    return this.itemsAnalysis.reduce((sum, item) => {
      const suggestedQty = item.suggestedQuantity || item.orderedQuantity || 0;
      const unitPrice = item.unitPrice || 0;
      return sum + (suggestedQty * unitPrice);
    }, 0);
  }

  get unitsDifference(): number {
    return this.totalSuggestedUnits - this.totalOrderedUnits;
  }

  get valueDifference(): number {
    return this.suggestedTotalValue - this.reviewSummary.totalValue;
  }

  get hasQuantityDifference(): boolean {
    return this.itemsAnalysis.some(item => item.orderedQuantity !== item.suggestedQuantity);
  }

  get itemsWithDifference(): number {
    return this.itemsAnalysis.filter(item => item.orderedQuantity !== item.suggestedQuantity).length;
  }

  // MOV related
  get movRequired(): number | null {
    // Prefer review_summary value, fall back to supplier_constraints
    return this.reviewSummary.mov_required ?? this.supplierConstraints.min_order_value ?? null;
  }

  get movSatisfied(): boolean {
    return this.reviewSummary.mov_satisfied !== false;
  }

  get movShortfall(): number | null {
    return this.reviewSummary.mov_shortfall;
  }

  get movCardClass(): string {
    if (!this.movRequired) return '';
    return this.movSatisfied ? 'constraint-success' : 'constraint-error';
  }

  get movIconColor(): string {
    if (!this.movRequired) return 'grey';
    return this.movSatisfied ? 'success' : 'error';
  }

  get movIcon(): string {
    if (!this.movRequired) return 'mdi-minus-circle-outline';
    return this.movSatisfied ? 'mdi-check-circle' : 'mdi-alert-circle';
  }

  get movTextClass(): string {
    if (!this.movRequired) return 'text--secondary';
    return this.movSatisfied ? 'success--text' : 'error--text';
  }

  get movStatusText(): string {
    if (!this.movRequired) return 'No minimum required';
    return this.movSatisfied ? 'Met' : 'Not Met';
  }

  // Stockout related
  get criticalStockoutCount(): number {
    return this.reviewSummary.critical_stockout_items || 0;
  }

  get stockoutCardClass(): string {
    return this.criticalStockoutCount > 0 ? 'constraint-error' : 'constraint-success';
  }

  get stockoutIconColor(): string {
    return this.criticalStockoutCount > 0 ? 'error' : 'success';
  }

  get stockoutIcon(): string {
    return this.criticalStockoutCount > 0 ? 'mdi-alert-circle' : 'mdi-check-circle';
  }

  get stockoutTextClass(): string {
    return this.criticalStockoutCount > 0 ? 'error--text' : 'success--text';
  }

  get stockoutStatusText(): string {
    return this.criticalStockoutCount > 0 ? `${this.criticalStockoutCount} at risk` : 'All items OK';
  }

  // MOQ related
  get moqViolationCount(): number {
    return this.itemsAnalysis.filter(item =>
      item.constraints?.moq && !item.constraints.moq.satisfied
    ).length;
  }

  get moqCardClass(): string {
    return this.moqViolationCount > 0 ? 'constraint-error' : 'constraint-success';
  }

  get moqIconColor(): string {
    return this.moqViolationCount > 0 ? 'error' : 'success';
  }

  get moqIcon(): string {
    return this.moqViolationCount > 0 ? 'mdi-alert-circle' : 'mdi-check-circle';
  }

  get moqTextClass(): string {
    return this.moqViolationCount > 0 ? 'error--text' : 'success--text';
  }

  get moqStatusText(): string {
    return this.moqViolationCount > 0 ? `${this.moqViolationCount} violations` : 'All items compliant';
  }

  // Status related
  get hasBlockingErrors(): boolean {
    return this.reviewSummary.hasErrors || false;
  }

  get hasWarnings(): boolean {
    return this.reviewSummary.hasWarnings || false;
  }

  get statusColor(): string {
    if (this.hasBlockingErrors) return 'error';
    if (this.hasWarnings || !this.movSatisfied || this.criticalStockoutCount > 0) return 'warning';
    return 'success';
  }

  get statusIcon(): string {
    if (this.hasBlockingErrors) return 'mdi-alert-circle';
    if (this.hasWarnings || !this.movSatisfied || this.criticalStockoutCount > 0) return 'mdi-alert';
    return 'mdi-check-circle';
  }

  get statusLabel(): string {
    if (this.hasBlockingErrors) return 'Issues Found';
    if (this.hasWarnings || !this.movSatisfied || this.criticalStockoutCount > 0) return 'Needs Review';
    return 'Ready';
  }

  // INSIGHT section computed properties
  get filteredComparisonItems(): ItemAnalysis[] {
    if (this.itemFilter === 'diff') {
      return this.itemsAnalysis.filter(item => item.orderedQuantity !== item.suggestedQuantity);
    }
    return this.itemsAnalysis;
  }

  // CONCLUSION section computed properties
  get conclusionColor(): string {
    if (this.hasBlockingErrors) return 'error';
    if (!this.movSatisfied || this.criticalStockoutCount > 0 || this.hasQuantityDifference) return 'warning';
    return 'success';
  }

  get conclusionAlertType(): string {
    if (this.hasBlockingErrors) return 'error';
    if (!this.movSatisfied || this.criticalStockoutCount > 0) return 'warning';
    if (this.hasQuantityDifference) return 'info';
    return 'success';
  }

  get conclusionTitle(): string {
    if (this.hasBlockingErrors) return 'Issues Found - Action Required';
    if (!this.movSatisfied) return 'Minimum Order Value Not Met';
    if (this.criticalStockoutCount > 0) return 'Stockout Risk Detected';
    if (this.hasQuantityDifference) return 'Review AI Suggestions';
    return 'Order Ready for Creation';
  }

  get conclusionMessage(): string {
    if (this.hasBlockingErrors) {
      return 'Please resolve the issues above before creating the purchase order.';
    }
    if (!this.movSatisfied) {
      return `Your order total of ${this.formatCurrency(this.reviewSummary.totalValue)} is ${this.formatCurrency(this.movShortfall || 0)} below the supplier's minimum of ${this.formatCurrency(this.movRequired || 0)}.`;
    }
    if (this.criticalStockoutCount > 0) {
      return `${this.criticalStockoutCount} item${this.criticalStockoutCount > 1 ? 's have' : ' has'} critical stockout risk. Consider prioritizing these items.`;
    }
    if (this.hasQuantityDifference) {
      return `AI analysis suggests different quantities for ${this.itemsWithDifference} item${this.itemsWithDifference > 1 ? 's' : ''}. Review the suggestions or proceed with your quantities.`;
    }
    return 'Your order quantities align with AI recommendations. All constraints are satisfied.';
  }

  // Finding card computed properties
  get movFindingClass(): string {
    if (!this.movRequired) return 'finding-neutral';
    return this.movSatisfied ? 'finding-success' : 'finding-error';
  }

  get movFindingText(): string {
    if (!this.movRequired) return 'No minimum requirement set';
    if (this.movSatisfied) {
      return `Met - Order value exceeds ${this.formatCurrency(this.movRequired)}`;
    }
    return `Short by ${this.formatCurrency(this.movShortfall || 0)}`;
  }

  get stockoutFindingClass(): string {
    return this.criticalStockoutCount > 0 ? 'finding-error' : 'finding-success';
  }

  get stockoutFindingText(): string {
    if (this.criticalStockoutCount === 0) {
      return 'All items have adequate stock coverage';
    }
    return `${this.criticalStockoutCount} item${this.criticalStockoutCount > 1 ? 's' : ''} at risk of stockout`;
  }

  get qtyFindingClass(): string {
    if (!this.hasQuantityDifference) return 'finding-success';
    return 'finding-warning';
  }

  get qtyIconColor(): string {
    if (!this.hasQuantityDifference) return 'success';
    return 'warning';
  }

  get qtyIcon(): string {
    if (!this.hasQuantityDifference) return 'mdi-check-circle';
    return 'mdi-swap-horizontal';
  }

  get qtyTextClass(): string {
    if (!this.hasQuantityDifference) return 'success--text';
    return 'warning--text';
  }

  get qtyFindingText(): string {
    if (!this.hasQuantityDifference) {
      return 'Your quantities match AI suggestions';
    }
    const totalDiff = Math.abs(this.unitsDifference);
    const direction = this.unitsDifference > 0 ? 'more' : 'fewer';
    return `AI suggests ${totalDiff} ${direction} units total`;
  }

  // Methods
  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  /**
   * Format AI insight text to HTML for display.
   * Converts markdown-like formatting to HTML.
   */
  formatAiInsight(text: string): string {
    if (!text) return '';

    // Escape HTML entities first
    let formatted = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Convert markdown-style formatting
    // Bold: **text** or __text__
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // Convert bullet points: lines starting with - or *
    formatted = formatted.replace(/^[\-\*]\s+(.*)$/gm, '<li>$1</li>');

    // Wrap consecutive <li> tags in <ul>
    formatted = formatted.replace(/(<li>.*<\/li>\n?)+/g, '<ul class="ai-insight-list">$&</ul>');

    // Convert line breaks to <br> (but not inside lists)
    formatted = formatted.replace(/\n(?!<)/g, '<br>');

    // Clean up double <br> tags
    formatted = formatted.replace(/<br><br>/g, '<br>');

    return formatted;
  }

  getComparisonRowClass(item: ItemAnalysis): string {
    if (item.orderedQuantity === item.suggestedQuantity) return '';
    if (item.stockout_risk?.urgency_level === 'critical') return 'comparison-row-critical';
    return 'comparison-row-diff';
  }

  getInsightReason(item: ItemAnalysis): string {
    if (item.orderedQuantity === item.suggestedQuantity) {
      return 'Quantity matches demand forecast';
    }

    const diff = item.suggestedQuantity - item.orderedQuantity;
    const reasons: string[] = [];

    // Check stockout risk
    if (item.stockout_risk) {
      if (item.stockout_risk.urgency_level === 'critical') {
        reasons.push(`Critical: only ${Math.round(item.stockout_risk.days_until_stockout)} days stock`);
      } else {
        reasons.push(`Low stock: ${Math.round(item.stockout_risk.days_until_stockout)} days coverage`);
      }
    }

    // Check sales trend
    if (item.historical?.sales_trend === 'growing' && diff > 0) {
      reasons.push('Sales trending up');
    } else if (item.historical?.sales_trend === 'declining' && diff < 0) {
      reasons.push('Sales declining');
    }

    // Check constraints
    if (item.constraints?.moq && !item.constraints.moq.satisfied) {
      reasons.push(`Below MOQ of ${item.constraints.moq.value}`);
    }

    // Default reason based on difference
    if (reasons.length === 0) {
      if (diff > 0) {
        reasons.push('Based on demand forecast');
      } else {
        reasons.push('Reduce to match demand');
      }
    }

    return reasons.join('; ');
  }

  getCoverageDaysClass(days: number): string {
    if (!days || days < 7) return 'error--text';
    if (days < 14) return 'warning--text';
    return '';
  }

  formatCoverageDays(item: ItemAnalysis): string {
    const days = item.coverageDays || item.estimatedCoverage || 0;
    if (days === 0) return '-';
    return `${Math.round(days)} days`;
  }

  getAvgMonthlySales(item: ItemAnalysis): string {
    // Try multiple possible field names from backend
    const sales = item.historical?.avg_monthly_sales
      || item.avgMonthlySales as number | undefined
      || item.avg_monthly_units as number | undefined;
    if (sales !== undefined && sales !== null) {
      return String(Math.round(Number(sales)));
    }
    return '-';
  }

  getItemTrend(item: ItemAnalysis): string | undefined {
    // Try multiple possible field names from backend
    return item.historical?.sales_trend
      || item.salesTrend as string | undefined
      || item.trend as string | undefined;
  }

  getItemMoq(item: ItemAnalysis): string {
    // Try multiple possible field names from backend
    const moq = item.constraints?.moq?.value
      || item.item_moq as number | undefined
      || item.minOrderQty as number | undefined
      || item.min_order_qty as number | undefined;
    if (moq !== undefined && moq !== null && Number(moq) > 0) {
      return String(moq);
    }
    return '-';
  }

  getItemRowClass(item: ItemAnalysis): string {
    if (item.stockout_risk?.urgency_level === 'critical') {
      return 'item-row-critical';
    }
    if (item.stockout_risk || (item.coverageDays && item.coverageDays < 14)) {
      return 'item-row-warning';
    }
    return '';
  }

  getTrendIcon(trend?: string): string {
    switch (trend) {
      case 'growing':
        return 'mdi-trending-up';
      case 'declining':
        return 'mdi-trending-down';
      default:
        return 'mdi-trending-neutral';
    }
  }

  getTrendColor(trend?: string): string {
    switch (trend) {
      case 'growing':
        return 'success';
      case 'declining':
        return 'error';
      default:
        return 'grey';
    }
  }

  handleBackToEdit(): void {
    this.$emit('close');
  }

  handleConfirmCreate(): void {
    this.$emit('confirm', {
      supplier_id: this.supplierId,
      supplier_name: this.supplierName,
      items: this.itemsAnalysis,
    });
  }

  handleApplyAISuggestions(): void {
    // Emit event to update quantities in the buying table
    const updates = this.itemsAnalysis
      .filter(item => item.orderedQuantity !== item.suggestedQuantity)
      .map(item => ({
        sku: item.sku,
        productName: item.productName,
        currentQty: item.orderedQuantity,
        suggestedQty: item.suggestedQuantity,
      }));

    EventBus.$emit('po-review:apply-suggestions', updates);

    // Show confirmation
    EventBus.$emit('show-snackbar', {
      message: `Applied AI suggestions to ${updates.length} item${updates.length === 1 ? '' : 's'}`,
      color: 'success'
    });

    // Go back to edit mode
    this.$emit('close');
  }
}
